import { DEMO_DATA_PATH, DEMO_DATE, DEMO_LABEL, DEMO_NOW, DEMO_SINCE, DEMO_UNTIL, type DemoManifest } from "../shared/demo";
import { WINDOW_MS, type WindowKey } from "../shared/permalink";
import type { FiresResponse } from "../shared/types";

let fires: Promise<FiresResponse> | undefined;

/** Static payloads are fetched once per page; window changes stay in memory. */
export async function demoFires(window: WindowKey): Promise<FiresResponse> {
  fires ??= fetch(`${DEMO_DATA_PATH}/fires.json`).then(async (res) => {
    if (!res.ok || !res.headers.get("content-type")?.includes("json")) throw new Error("Demo archive is not available yet.");
    return await res.json() as FiresResponse;
  });
  const all = await fires;
  const since = new Date(Math.max(Date.parse(DEMO_SINCE), DEMO_NOW + 1 - WINDOW_MS[window])).toISOString();
  return {
    ...all,
    window: { since, until: DEMO_UNTIL },
    incidents: all.incidents.filter((i) => i.last_seen >= since),
    heat: all.heat.filter((p) => p[3] >= since),
  };
}

export function demoJsonPath(path: string): string {
  return `${DEMO_DATA_PATH}/${path}.json`;
}

export function installDemoChrome(): void {
  document.body.classList.add("demo");
  const chip = document.getElementById("demo-chip")!;
  chip.classList.remove("hidden");
  chip.textContent = `DEMO · ${DEMO_LABEL} · UTC`;
  document.querySelector(".claim")!.textContent = "Recorded satellite-observed fire activity across Europe";
  for (const [id, href] of [["nav-home", "/demo"], ["nav-map", "/demo"], ["nav-events", "/demo/events"]]) {
    document.getElementById(id)?.setAttribute("href", href);
  }
  document.getElementById("theater-live")!.textContent = "View on demo map";
  for (const key of ["48h", "7d"]) document.querySelector(`[data-window="${key}"]`)?.setAttribute("hidden", "");
  // These sources expose current conditions. They must not enter a frozen historical view.
  for (const layer of ["wind", "sat", "risk", "lst", "fwi", "ba"]) {
    const button = document.querySelector<HTMLButtonElement>(`[data-layer="${layer}"]`);
    if (!button) continue;
    button.disabled = true;
    button.dataset.tip = "This live layer is unavailable in the recorded demo. Historical satellite snapshots remain in each fire's details.";
  }
  document.getElementById("playhead")!.dataset.tip = "Jump to the end of this recorded window (UTC)";
  void fetch(`${DEMO_DATA_PATH}/manifest.json`).then(async (res) => {
    if (!res.ok || !res.headers.get("content-type")?.includes("json")) return;
    const manifest = await res.json() as DemoManifest;
    if (manifest.date !== DEMO_DATE) throw new Error("Unexpected demo date");
    const missing = manifest.sources.filter((s) => s.status !== "complete").map((s) => s.source);
    const note = document.getElementById("demo-source-note")!;
    note.textContent = missing.length
      ? `Archive coverage: ${manifest.sources.filter((s) => s.status === "complete").map((s) => `${s.source}: ${s.count.toLocaleString()}`).join(", ")}. Archive gaps: ${missing.join(", ")}.`
      : `Recorded ${DEMO_LABEL} (UTC). ${manifest.sources.map((s) => `${s.source}: ${s.count.toLocaleString()}`).join(", ")}.`;
    note.classList.remove("hidden");
  }).catch(() => {});
}
